import useEventCallback from '@unifyapps/hooks/useEventCallback';
import { type LocaleEnum, RTL_LANGUAGES } from '@unifyapps/i18n/const';
import { LOCALE_SEARCH_PARAMS_KEY } from '@unifyapps/network/auth/const';
import { getGetApiUserContextQueryKey } from '@unifyapps/network/generated/user-context-rest-api/user-context-rest-api';
import { useUpdateLocale } from '@unifyapps/network/generated/user-rest-api/user-rest-api';
import { useGetMutationMeta } from '@unifyapps/network/hooks/useGetMutationMeta';
import useMutationHelpers from '../../../../../../hooks/useMutationHelpers';
import { useGlobalAppStateStore } from '../../../../../stores/app/GlobalAppStateStore';
import { InterfaceModes } from '../../../../../stores/InterfaceStore';
import InterfaceHelper from '../../../../../stores/InterfaceStore/helper/InterfaceHelper';
import { useQueryParams } from '../../../../useQueryParams';

export function useUpdateLocaleForPrivateInterface() {
  const mode = useGlobalAppStateStore().hooks.useMode();
  const { onInvalidateQueries } = useMutationHelpers();
  const { setQueryParam, getQueryParam } = useQueryParams();
  const userContext = useGlobalAppStateStore().hooks.useUserContext();
  const currentLocale = userContext.locale;

  // useUpdateLocale is auto-generated by Orval and doesn't use useGetMutationMeta internally.
  // We need to manually pass the mutation meta (which includes isPublicInterface for anonymous sessions)
  // so the request is routed through /api-endpoint/external/ for private anonymous users.
  const meta = useGetMutationMeta();
  const { mutate: updateLocale } = useUpdateLocale({
    request: { meta },
  });

  const interfaceRecord = useGlobalAppStateStore().hooks.record.useDetails();
  const allowedLocale = InterfaceHelper.getAllLocalesSet(interfaceRecord);

  const updateLocaleForPrivateInterface = useEventCallback((locale: string) => {
    if (
      currentLocale === locale ||
      mode !== InterfaceModes.RUNNER ||
      !allowedLocale.has(locale as LocaleEnum)
    ) {
      return Promise.resolve();
    }

    updateLocale(
      { params: { locale } },
      {
        onSuccess: () => {
          if (getQueryParam(LOCALE_SEARCH_PARAMS_KEY)) {
            //if locale was decided from search params, then remove it from search params
            //and use user locale now after the action is executed
            setQueryParam(LOCALE_SEARCH_PARAMS_KEY, null);
          }
          onInvalidateQueries([getGetApiUserContextQueryKey()]);
          document.documentElement.setAttribute('lang', locale);
          document.documentElement.setAttribute(
            'dir',
            RTL_LANGUAGES.has(locale as LocaleEnum) ? 'rtl' : 'ltr',
          );
        },
      },
    );
    return Promise.resolve();
  });

  return { updateLocaleForPrivateInterface };
}
